import React, { useEffect, useState } from 'react'
import { useUnit } from 'effector-react'
import { Alert, Button, Flex, Modal, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { TableRowSelection } from 'antd/lib/table/interface'
import { formatDigits } from '../../../libs/utils'
import { groupClientPurseMounted } from './model'
import { ClientPurseItem } from '../groupedClientPurses/types'
import { $isRequestSuccess } from '../../../features/notifications'
import { clientGroupedPursesListMounted } from 'src/views/widgets/groupedClientPurses/model'

type Props = {
  purses: ClientPurseItem[]
  clientId: string
}

export const getPursesId = (data: ClientPurseItem[]) => {
  const mainValues = data
    .filter((item) => item.clientPurseId)
    .map((item) => item.clientPurseId as number)
  const childrenValues: number[] = []
  data.forEach((item) => {
    if (item.children?.length !== 0) {
      item.children?.forEach((child) => {
        child.clientPurseId && childrenValues.push(child.clientPurseId)
      })
    }
  })

  return [...mainValues, ...childrenValues]
}

export const validatePurses = (data: ClientPurseItem[]) => {
  const pursesWithoutGroup = data.filter((item) => !item.purseGroupId)
  const pursesWithGroup = data.filter(
    (item) => item.purseGroupId && item.clientPurseId,
  )

  if (pursesWithoutGroup.length > 0 && pursesWithGroup.length > 0) {
    return 'First, you need to disconnect the desired wallet from the group.'
  }

  return null
}

const findMain = (items: ClientPurseItem[]): ClientPurseItem | undefined => {
  for (const item of items) {
    if (item.isMainInGroup) return item
    if (item.children?.length) {
      const found = findMain(item.children)
      if (found) return found
    }
  }
}

export const ConnectPurseModal: React.FC<Props> = ({ purses, clientId }) => {
  const [isVisible, setVisible] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedRows, setSelectedRows] = React.useState<ClientPurseItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])

  const isRequestSuccess = useUnit($isRequestSuccess)

  const mainPurse = findMain(purses)

  const columns: ColumnsType<ClientPurseItem> = [
    {
      title: 'Trust',
      dataIndex: 'isTrusted',
      width: 100,
      render: (isTrusted: boolean) => <div>{isTrusted ? 'yes' : 'no'}</div>,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Group ID',
      dataIndex: 'purseGroupId',
    },
    {
      title: 'Bank',
      dataIndex: 'bankName',
      render: (text: string) => <strong>{text}</strong>,
      defaultSortOrder: 'ascend',
      // sorter: (a, b) => a.gatewayName.localeCompare(b.gatewayName),
    },
    {
      title: 'Purse',
      render: (item: ClientPurseItem) => <strong>{item.mask}</strong>,
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      render: (text: string | null) => <strong>{text ? text : ''}</strong>,
    },
    {
      title: 'Deposit',
      render: (item: ClientPurseItem) => (
        <span style={{ color: 'green' }}>
          {`${formatDigits(item.turnover.deposit)} ${item?.turnover.currency} ${
            item.turnover.depositUSD && item?.turnover.currency !== 'USD'
              ? `(${item.turnover.depositUSD}$)`
              : ''
          }`}
        </span>
      ),
    },
    {
      title: 'Withdrawal',
      render: (item: ClientPurseItem) => (
        <span style={{ color: 'red' }}>
          {`${formatDigits(item.turnover.withdrawal)} ${
            item?.turnover.currency
          } ${
            item.turnover.withdrawalUSD && item?.turnover.currency !== 'USD'
              ? `(${item.turnover.withdrawalUSD}$)`
              : ''
          }`}
        </span>
      ),
    },
    {
      title: 'Total',
      render: (item: ClientPurseItem) => (
        <span style={{ color: item.turnover.total >= 0 ? 'green' : 'red' }}>
          {formatDigits(item.turnover.total)} {item?.turnover.currency}{' '}
        </span>
      ),
    },
  ]

  const handleSubmit = () => {
    const isGroupMerge = purses.every((item) => item.children && item.children.length > 0)
    const mainPurseId = selectedRows[0]?.clientPurseId

    if (!mainPurseId) {
      setErrorMessage('Cannot use this selection: missing clientPurseId')
      return
    }

    if (isGroupMerge) {
      groupClientPurseMounted({
        groupsIds: purses
          .filter((item) => item.purseGroupId)
          .map((item) => item.purseGroupId as number),
        mainPurseId,
      })
    } else {
      validatePurses(purses)
        ? setErrorMessage(validatePurses(purses))
        : groupClientPurseMounted({
            clientPursesIds: getPursesId(purses),
            mainPurseId,
          })
    }
  }

  const handleOpenModal = () => {
    setVisible(true)
    setErrorMessage(null)
    setSelectedRows(mainPurse ? [mainPurse] : [])
    setSelectedRowKeys(mainPurse ? [mainPurse.key] : [])
  }

  const handleCloseModal = () => {
    setVisible(false)
    setErrorMessage(null)
    setSelectedRows([])
    setSelectedRowKeys([])
  }

  useEffect(() => {
    if (isRequestSuccess) {
      handleCloseModal()
      clientGroupedPursesListMounted({
        clientId,
        orderBy: 'updatedAt',
        orderDirection: '-',
      })
    }
  }, [isRequestSuccess, clientId])

  const rowSelection: TableRowSelection<ClientPurseItem> = {
    checkStrictly: false,
    type: 'radio',
    selectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: Boolean(record.purseGroupId) && !record.clientPurseId,
    }),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys)
      setSelectedRows(selectedRows)
    },
  }

  return (
    <>
      <Button
        color="purple"
        variant="solid"
        className="mb-3"
        onClick={handleOpenModal}
        disabled={purses.length < 2}
      >
        Connect purses
      </Button>

      <Modal
        title="Connect purses"
        open={isVisible}
        onCancel={handleCloseModal}
        centered
        wrapClassName="ant_modal"
        width={1300}
        footer={null}
      >
        {errorMessage && (
          <Alert
            type="error"
            message={errorMessage}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          dataSource={purses}
          rowKey="key"
          bordered={true}
          scroll={{ x: true }}
          columns={columns}
          pagination={false}
          rowSelection={rowSelection}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
        />
        <br />
        <Flex justify="flex-end" gap={8}>
          <Button
            color="primary"
            variant="solid"
            onClick={handleSubmit}
            disabled={selectedRows.length === 0}
          >
            Ok
          </Button>
          <Button onClick={handleCloseModal}>Cancel</Button>
        </Flex>
      </Modal>
    </>
  )
}
